import { useHCaptchaContext, usePaymentContext, usePricesContext } from '@fff-web/fff-payments';
import { track } from '@fff-web/fff-analytics';
import { showWarningToast } from '@fff-web/fff-ui-shared/Toast';
import { useDecision } from '@optimizely/react-sdk';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSharedContext } from '../../pages/GTBCustomize/ContextProviders/SharedContext';
import { getValidateCustomizations } from '../../utils/Data/customizeConnector';
import { isResponseError } from '../../utils/Data/dataTypes';
import { isCallSuccessful } from '../../utils/Data/dataUtilities';
import { firePurchaseEvent } from '../../utils/Data/marketing';
import { postSaveAdditionalChoices } from '../../utils/Data/newMemberConnector';
import { createGTBCheckoutRegisterRequest, postRegister } from '../../utils/Data/registerConnector';
import { trackFMCOptimizelyExperiment } from '../../utils/Optimizely';
import GROWTH_FULL_MEMBER_CUSTOMIZE_EXPERIMENT_1_0_0_VARIANTS from '../../utils/Optimizely/experiments';
import { replaceParam } from '../../utils/queryParamsUtils';
import PREVIEW_CUSTOMIZE_SLUG from '../YourChoices/constants';
import { SELECT_PAYMENT_TYPE } from '../../utils/segment/constants';
import { usePublicShopContext } from '../../pages/GTBCustomize/ContextProviders/PublicShopContext';
import { choicesToLineItems } from '../../utils/Data/marketingUtilitites';

const REGISTRATION_ERROR_MESSAGE = 'There was an error processing your request. Please contact customer support for further assistance.';
const PURCHASE_EVENT = 'Subscribe';
const LOGGER_MESSAGE = 'GTB - PURCHASE EVENT';
const customizationChoicesInvalidMessage = (query: string) => ([
  { text: 'One or more of the items you have chosen are not available for checkout anymore.' },
  { text: 'Please change your selection', url: `${PREVIEW_CUSTOMIZE_SLUG}?${replaceParam(query, 'q', '1')}` },
  { text: 'or call Customer Service at 1-855-313-6267 for additional help.' },
]);
const GTBTY_PATH = '/gtb-thank-you/shipping';

const RegisterUser = () => {
  const { choices } = usePublicShopContext();
  const {
    prospect, planCode, promoCode, setLoading, campaign, timerStatus, countries,
  } = useSharedContext();
  const { campaignId, campaignName } = campaign;
  const { hCaptchaToken, setHCaptchaToken, setExecuteHCaptcha } = useHCaptchaContext();
  const {
    recurlyToken,
    setRecurlyToken,
    billingMethod,
    isAltPaymentOpen,
  } = usePaymentContext();
  const { total } = usePricesContext();

  const [FMCDecision] = useDecision(
    GROWTH_FULL_MEMBER_CUSTOMIZE_EXPERIMENT_1_0_0_VARIANTS.FEATURE_FLAG_NAME,
  );

  const lineItems = choicesToLineItems(choices);

  const [customizationsValid, setCustomizationsValid] = useState(false);
  const [userCreated, setUserCreated] = useState(false);
  const [additionalChoicesSaved, setAdditionalChoicesSaved] = useState(false);

  const resetRegistrationFlow = () => {
    setExecuteHCaptcha(false);
    setHCaptchaToken('');
    setRecurlyToken(null);
    setCustomizationsValid(false);
    setUserCreated(false);
    setAdditionalChoicesSaved(false);
    setLoading(false);
  };

  const { search } = useLocation();

  useEffect(() => {
    if (!hCaptchaToken) {
      setLoading(false);
    }
  }, [hCaptchaToken]);

  // handle paypal + apple pay payment method cancel
  useEffect(() => {
    if (!isAltPaymentOpen) {
      setLoading(false);
    }
  }, [isAltPaymentOpen]);

  // make call to check customization choices if recurlyToken is set
  useEffect(() => {
    if (recurlyToken && !hCaptchaToken) {
      setExecuteHCaptcha(true);
      return;
    }

    if (recurlyToken && hCaptchaToken) {
      getValidateCustomizations()
        .then((response) => {
          if (isCallSuccessful(response?.status)) {
            setCustomizationsValid(true);
          } else {
            showWarningToast('', customizationChoicesInvalidMessage(search));
            resetRegistrationFlow();
          }
        })
        .catch(() => {
          showWarningToast('', customizationChoicesInvalidMessage(search));
          resetRegistrationFlow();
        });
    }
  }, [recurlyToken, hCaptchaToken]);

  // make call to register user if setCustomizationsValid
  useEffect(() => {
    if (customizationsValid) {
      track({
        trackName: SELECT_PAYMENT_TYPE,
        properties: {
          campaign_id: campaignId,
          campaign_name: campaignName,
          customize_experience_type: 'preview_customize',
          plan_code: planCode,
          payment_type: billingMethod,
        },
      });
      const registerRequest = createGTBCheckoutRegisterRequest(
        recurlyToken as string, prospect, countries, billingMethod, planCode as string,
      );
      postRegister(registerRequest, hCaptchaToken as string)
        .then((response) => {
          if (isCallSuccessful(response?.status)) {
            const { subscriptionUuid, shopUserId } = response?.data;
            firePurchaseEvent(
              PURCHASE_EVENT,
              LOGGER_MESSAGE,
              {
                couponCode: promoCode,
                email: prospect.email,
                planCode,
                subscriptionUuid,
                total,
                userId: shopUserId,
                lineItems,
              },
            );
            trackFMCOptimizelyExperiment(FMCDecision, timerStatus, planCode);
            setUserCreated(true);
          } else {
            resetRegistrationFlow();
            if (response.status === 500) {
              return showWarningToast(REGISTRATION_ERROR_MESSAGE);
            }
            if (isResponseError(response)) {
              return showWarningToast(response.error.data.message);
            }
          }
          return setUserCreated(false);
        })
        .catch(() => {
          showWarningToast(REGISTRATION_ERROR_MESSAGE);
          resetRegistrationFlow();
        });
    }
  }, [customizationsValid]);

  // make call to save additional choices
  useEffect(() => {
    if (userCreated) {
      postSaveAdditionalChoices()
        .then((response) => {
          if (isCallSuccessful(response?.status)) {
            setAdditionalChoicesSaved(true);
          }
        })
        .catch(() => {
          // TODO: try again?
        });
    }
  }, [userCreated]);

  // redirect to GTBTY page
  useEffect(() => {
    if (additionalChoicesSaved) {
      window.location.assign(GTBTY_PATH);
    }
  }, [additionalChoicesSaved]);

  return null;
};

export default RegisterUser;
export { GTBTY_PATH, customizationChoicesInvalidMessage };
